import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TUNING_DB } from "@/data/tuningDb";
import { api } from "@/lib/api";
import { ChevronDown, Gauge, Zap, ArrowRight, Cpu, Sparkles, Loader2, List, PenLine } from "lucide-react";

const Dropdown = ({ label, value, options, onChange, disabled, testid }) => (
  <div className="relative">
    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{label}</label>
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-white/5 border border-white/10 rounded-sm px-4 py-3 pr-10 font-display uppercase tracking-wide text-sm focus:outline-none focus:border-aurora-cyan/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
      >
        <option value="">{disabled ? "—" : "Sélectionner"}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown className="w-4 h-4 text-aurora-cyan absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  </div>
);

const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{label}</label>
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-display uppercase tracking-wide text-sm focus:outline-none focus:border-aurora-cyan/60 transition-colors placeholder:text-zinc-600 placeholder:normal-case placeholder:tracking-normal"
    />
  </div>
);

const Bar = ({ label, base, total, max, gain, accent }) => {
  const basePct = Math.round((base / max) * 100);
  const totalPct = Math.round((total / max) * 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
        <span className="font-mono text-sm">
          <span className="text-zinc-400">{base}</span>
          <ArrowRight className="inline w-3 h-3 mx-1 text-zinc-500" />
          <span className="font-bold text-aurora-cyan">{total}</span>
          <span className="text-signal-success font-bold ml-2">+{gain}</span>
        </span>
      </div>
      <div className="relative h-3 rounded-full bg-white/5 overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-white/15 rounded-full" style={{ width: `${basePct}%` }} />
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ background: accent }}
          initial={{ width: `${basePct}%` }}
          animate={{ width: `${totalPct}%` }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.3, 1] }}
        />
      </div>
    </div>
  );
};

const CONF = { low: "Faible", medium: "Moyenne", high: "Élevée" };

export const TuningSelector = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("list"); // 'list' | 'free'

  // list mode
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [engine, setEngine] = useState("");

  // free / AI mode
  const [fBrand, setFBrand] = useState("");
  const [fModel, setFModel] = useState("");
  const [fEngine, setFEngine] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const brands = useMemo(() => Object.keys(TUNING_DB), []);
  const models = useMemo(() => (brand ? Object.keys(TUNING_DB[brand]) : []), [brand]);
  const engines = useMemo(() => (brand && model ? Object.keys(TUNING_DB[brand][model]) : []), [brand, model]);
  const curated = brand && model && engine ? TUNING_DB[brand][model][engine] : null;

  const estimateAI = async () => {
    if (!fBrand.trim()) { setErr("Indiquez au moins la marque."); return; }
    setErr(""); setLoading(true); setAiResult(null);
    try {
      const { data } = await api.post("/public/tuning_estimate", { brand: fBrand, model: fModel, engine: fEngine });
      setAiResult(data);
    } catch (e) {
      setErr("Estimation indisponible pour le moment, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  // Normalize whichever result is active
  const result = mode === "list"
    ? (curated ? { ...curated, ghp: curated.ghp, gnm: curated.gnm, brand, model, source: "db" } : null)
    : (aiResult ? { hp: aiResult.base_hp, nm: aiResult.base_nm, ghp: aiResult.gain_hp, gnm: aiResult.gain_nm, ecu: aiResult.ecu, brand: aiResult.brand, model: aiResult.model || aiResult.engine, confidence: aiResult.confidence, source: "ai" } : null);

  const switchMode = (m) => { setMode(m); setErr(""); };

  return (
    <section id="specs" className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="tuning-selector">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">// Spécifications</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">Découvrez vos gains</h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto text-sm">
            Sélectionnez votre véhicule (ou saisissez-le) et visualisez le potentiel Stage 1 estimé — toutes marques couvertes.
          </p>
        </div>

        {/* Mode switch */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex glass rounded-sm p-1">
            <button onClick={() => switchMode("list")} data-testid="mode-list-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest transition-colors ${mode === "list" ? "bg-aurora-cyan/15 text-aurora-cyan" : "text-zinc-500 hover:text-zinc-300"}`}>
              <List className="w-3.5 h-3.5" /> Liste populaire
            </button>
            <button onClick={() => switchMode("free")} data-testid="mode-free-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest transition-colors ${mode === "free" ? "bg-aurora-cyan/15 text-aurora-cyan" : "text-zinc-500 hover:text-zinc-300"}`}>
              <PenLine className="w-3.5 h-3.5" /> Toutes voitures · IA
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Selector */}
          <div className="glass rounded-md p-7">
            {mode === "list" ? (
              <div className="space-y-5">
                <Dropdown label="Marque" testid="select-brand" value={brand} options={brands}
                  onChange={(v) => { setBrand(v); setModel(""); setEngine(""); }} />
                <Dropdown label="Modèle" testid="select-model" value={model} options={models} disabled={!brand}
                  onChange={(v) => { setModel(v); setEngine(""); }} />
                <Dropdown label="Motorisation" testid="select-engine" value={engine} options={engines} disabled={!model}
                  onChange={setEngine} />
                <div className="flex items-center gap-2 font-mono text-[11px] text-zinc-500 uppercase tracking-wider pt-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-signal-success animate-pulse-glow" />
                  Estimations Stage 1 · usage hors voie publique
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <TextField label="Marque" testid="free-brand" value={fBrand} onChange={setFBrand} placeholder="ex: Tesla, Cupra, Jeep…" />
                <TextField label="Modèle" testid="free-model" value={fModel} onChange={setFModel} placeholder="ex: Model 3, Formentor…" />
                <TextField label="Motorisation" testid="free-engine" value={fEngine} onChange={setFEngine} placeholder="ex: 2.0 TDI 150 ch" />
                {err && <p data-testid="free-error" className="font-mono text-[11px] text-signal-error">{err}</p>}
                <button onClick={estimateAI} disabled={loading} data-testid="estimate-ai-btn"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-60">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Estimation…</> : <><Sparkles className="w-4 h-4" /> Estimer par IA</>}
                </button>
              </div>
            )}
          </div>

          {/* Result */}
          <div className="glass rounded-md p-7 relative overflow-hidden min-h-[300px]">
            <div className="m-stripe-bar absolute top-0 left-0" />
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div key={`${result.brand}-${result.model}-${result.source}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                  <div className="flex items-start justify-between mb-6 gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan truncate">{result.brand}</div>
                      <div className="font-display text-xl font-bold uppercase tracking-tight truncate">{result.model}</div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                      <Cpu className="w-4 h-4 text-aurora-cyan" /> {result.ecu}
                    </div>
                  </div>
                  <div className="space-y-6">
                    <Bar label="Puissance (ch)" base={result.hp} total={result.hp + result.ghp} gain={result.ghp} max={(result.hp + result.ghp) * 1.12 || 1} accent="linear-gradient(90deg,#0064B0,#5E3DA8)" />
                    <Bar label="Couple (Nm)" base={result.nm} total={result.nm + result.gnm} gain={result.gnm} max={(result.nm + result.gnm) * 1.12 || 1} accent="linear-gradient(90deg,#5E3DA8,#C8102E)" />
                  </div>
                  {result.source === "ai" && (
                    <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-aurora-purple">
                      <Sparkles className="w-3.5 h-3.5" /> Estimation IA · confiance {CONF[result.confidence] || "moyenne"}
                    </div>
                  )}
                  <button onClick={() => navigate("/login")} data-testid="specs-cta-btn"
                    className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm">
                    Traiter mon fichier <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[260px] flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 grid place-items-center border border-dashed border-aurora-cyan/30 rounded-md bg-aurora-cyan/5 mb-4">
                    {mode === "free" ? <Sparkles className="w-7 h-7 text-aurora-cyan" /> : <Gauge className="w-7 h-7 text-aurora-cyan" />}
                  </div>
                  <p className="text-zinc-400 text-sm max-w-xs">
                    {mode === "free"
                      ? "Saisissez votre véhicule et lancez l'estimation IA — toutes les voitures sont couvertes."
                      : "Sélectionnez une marque, un modèle et une motorisation pour révéler vos gains estimés."}
                  </p>
                  <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                    <Zap className="w-3.5 h-3.5 text-aurora-cyan" /> Stage 1 · puissance & couple
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
